import { useRef, useState, useId } from 'react';
import { CUSTOM_SCROLLBAR } from '@/lib/constants';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Link,
  Eye,
  Edit,
} from 'lucide-react';
import MarkdownRenderer from './markdown-renderer';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  label?: string;
}

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Enter description in Markdown format...',
  readOnly = false,
  label = 'Rich text editor',
}: RichTextEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPreview, setIsPreview] = useState(false);
  const editorId = useId();
  const toolbarId = `toolbar-${editorId}`;
  const textareaId = `textarea-${editorId}`;

  const insertMarkdown = (before: string, after: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    const newText = beforeText + before + selectedText + after + afterText;
    onChange(newText);

    // Set cursor position after the inserted text
    setTimeout(() => {
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertAtLineStart = (prefix: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // Find the start of the current line
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const lineEnd = value.indexOf('\n', end);
    const actualLineEnd = lineEnd === -1 ? value.length : lineEnd;

    const beforeLine = value.substring(0, lineStart);
    const line = value.substring(lineStart, actualLineEnd);
    const afterLine = value.substring(actualLineEnd);

    const newText = beforeLine + prefix + line + afterLine;
    onChange(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(lineStart + prefix.length, lineStart + prefix.length + line.length);
    }, 0);
  };

  const toolbarButtons = [
    { icon: Bold, action: () => insertMarkdown('**', '**'), title: 'Bold' },
    { icon: Italic, action: () => insertMarkdown('*', '*'), title: 'Italic' },
    { icon: Strikethrough, action: () => insertMarkdown('~~', '~~'), title: 'Strikethrough' },
    { icon: Code, action: () => insertMarkdown('`', '`'), title: 'Inline Code' },
    { icon: Heading1, action: () => insertAtLineStart('# '), title: 'Heading 1' },
    { icon: Heading2, action: () => insertAtLineStart('## '), title: 'Heading 2' },
    { icon: Heading3, action: () => insertAtLineStart('### '), title: 'Heading 3' },
    { icon: List, action: () => insertAtLineStart('- '), title: 'Bullet List' },
    { icon: ListOrdered, action: () => insertAtLineStart('1. '), title: 'Numbered List' },
    { icon: Quote, action: () => insertAtLineStart('> '), title: 'Quote' },
    {
      icon: Link,
      action: () => insertMarkdown('[', '](url)'),
      title: 'Link'
    },
  ];

  return (
    <div className="markdown-text-editor" role="group" aria-label={label}>
      {!readOnly && (
        <div
          id={toolbarId}
          className="flex items-center justify-between p-2 bg-surface border border-surface-border rounded-t-lg"
          role="toolbar"
          aria-label="Text formatting toolbar"
          aria-controls={textareaId}
        >
          <div className="flex flex-wrap gap-1" role="group" aria-label="Formatting options">
            {!isPreview && toolbarButtons.map((button, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  button.action();
                }}
                className="p-2 text-muted hover:text-white hover:bg-surface-hover rounded transition-colors focus:outline-2 focus:outline-primary"
                title={button.title}
                aria-label={button.title}
                type="button"
              >
                <button.icon className="w-4 h-4" aria-hidden="true" />
              </button>
            ))}
          </div>
          <div className="flex gap-1" role="group" aria-label="View mode toggle">
            <button
              onClick={() => setIsPreview(false)}
              className={`p-2 rounded transition-colors focus:outline-2 focus:outline-primary ${
                !isPreview
                  ? 'text-primary bg-primary/10'
                  : 'text-muted hover:text-white hover:bg-surface-hover'
              }`}
              title="Edit Mode"
              aria-label="Edit mode"
              aria-pressed={!isPreview}
              type="button"
            >
              <Edit className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              onClick={() => setIsPreview(true)}
              className={`p-2 rounded transition-colors focus:outline-2 focus:outline-primary ${
                isPreview
                  ? 'text-primary bg-primary/10'
                  : 'text-muted hover:text-white hover:bg-surface-hover'
              }`}
              title="Preview Mode"
              aria-label="Preview mode"
              aria-pressed={isPreview}
              type="button"
            >
              <Eye className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
      
      {isPreview && !readOnly ? (
        <div
          className="w-full min-h-[200px] max-h-[400px] p-4 bg-surface-input border border-surface-border rounded-b-lg border-t-0 overflow-y-auto"
          role="region"
          aria-label="Markdown preview"
          aria-live="polite"
        >
          {value ? (
            <MarkdownRenderer content={value} />
          ) : (
            <div className="text-muted italic">Nothing to preview</div>
          )}
        </div>
      ) : (
        <textarea
          id={textareaId}
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          aria-label={label}
          aria-describedby={!readOnly ? "markdown-tips" : undefined}
          className={`w-full min-h-[200px] max-h-[400px] p-4 bg-surface-input border border-surface-border ${
            readOnly ? 'rounded-lg' : 'rounded-b-lg border-t-0'
          } text-white placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary resize-y ${CUSTOM_SCROLLBAR}`}
        />
      )}
      
      {!readOnly && (
        <div id="markdown-tips" className="mt-2 text-xs text-muted">
          <span className="font-semibold">Markdown Tips:</span> Use toolbar buttons or type **bold**, *italic*, # Headers, - Lists, [links](url), `code`, ```code blocks```
        </div>
      )}
    </div>
  );
};
